// The screens in the person's language (chosen with the language button, top right). The screens are written in
// English; when another language is chosen, the English wording people read — menus, headings, buttons, labels, hints,
// table headings, messages, pop-ups — is swapped for that language's wording from its phrase file
// (web/public/i18n/<code>.json, built from tools/screen-phrases.mjs), as each screen is drawn, before it is shown.
//
// Never translated (stays exactly as it is):
//  - anything people type or have saved: typed boxes and their values (inputs, text areas, dropdown values);
//  - artwork, labels, swing tags, carton marks, CAD, drawings and images (SVG, canvas, previews);
//  - documents that leave Falcon: PO, tech pack, datasheets, packing lists, label packs (on screen and as PDFs);
//  - the Help Centre's text (English, or its own Chinese translation in help/zh/ when 中文 is chosen), and anything
//    marked translate="no" or data-no-tr.
// Wording without a translation stays English. Arabic and Urdu run right to left.
import { langInfo } from "../../shared/i18n/index.js";
import { currentLang, syncDeviceLang } from "./i18n.js";
import { state } from "./state.js";

/** Never translated, with everything inside it. */
export const NO_TRANSLATE = [
  "script", "style", "textarea", "svg", "canvas", "iframe", "video", "code", "pre", "kbd", "[contenteditable]", "[translate=no]", "[data-no-tr]",
  // Documents that leave Falcon (PO, tech pack, datasheets, landed costing sheets, order timeline document)
  ".falcon-sheet-frame", ".tp-document", ".po-document", ".tl-doc", ".tpd-page",
  // Labels, swing tags, carton marks, artwork and CAD
  ".lbl-svg", ".lbl-preview-sides", ".tp-label-preview", ".tp-bom-preview", ".pk-preview", ".tpd-label-svg", ".art-thumb", ".cad-stage",
  // The Help Centre's text (English, or its own Chinese pages)
  ".help-body", ".help-panel-body", ".help-results",
  // The language list itself (each language in its own words)
  ".falcon-lang-wrap",
].join(",");
const ATTRS = ["placeholder", "title", "aria-label", "alt"];

interface Template { re: RegExp; out: string; size: number; plural: Set<number> }
let active = "en";
let exact = new Map<string, string>();
let byWord = new Map<string, Template[]>();
let observer: MutationObserver | null = null;
/** What this file put on screen, so a change made here is not translated again. */
const doneText = new WeakMap<Text, string>();
const doneAttr = new WeakMap<Element, Record<string, string>>();

const norm = (s: string) => s.replace(/\s+/g, " ").trim();
const words = (s: string) => s.toLowerCase().match(/[a-z][a-z'’]{2,}/g) || [];
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function load(dict: Record<string, string>): void {
  exact = new Map();
  byWord = new Map();
  for (const [en, tr] of Object.entries(dict)) {
    if (!tr || tr === en) continue;
    if (!/\{\d+\}/.test(en)) { exact.set(en, tr); continue; }
    // "Showing {0} of {1}": the fixed words must say enough to be sure it is this wording.
    const fixed = en.replace(/\{\d+\}/g, " ");
    const ws = words(fixed);
    if (fixed.replace(/[^A-Za-z]/g, "").length < 4 || !ws.length) continue;
    // A value can be empty (a plural ending that isn't there: "1 record").
    const re = new RegExp(`^${en.split(/(\{\d+\})/).map((p) => (/^\{\d+\}$/.test(p) ? "(.*?)" : escRe(p))).join("")}$`, "s");
    const key = ws.reduce((a, b) => (b.length > a.length ? b : a));
    // "{0} record{1}": a value straight after letters is the English plural ending ("s" / "es"), not shown in
    // other languages.
    const plural = new Set([...en.matchAll(/[A-Za-z]\{(\d+)\}/g)].map((m) => Number(m[1])));
    const t: Template = { re, out: tr, size: fixed.length, plural };
    (byWord.get(key) || byWord.set(key, []).get(key)!).push(t);
  }
  for (const list of byWord.values()) list.sort((a, b) => b.size - a.size);
}

/** The wording of one piece of English text (spaces trimmed), or undefined when there is none. Values inside a
 *  phrase ("Showing {0} of {1}") are translated too when they are wording themselves (one level deep). */
function translateCore(core: string, depth = 0): string | undefined {
  const own = exact.get(core);
  if (own !== undefined) return own;
  // Every phrase with values that fits; the one with the most fixed wording wins, so a value never swallows a
  // sentence that has its own translation ("Last changed {0}" vs "{0}. Changing it signs out your other devices.").
  let best: { t: Template; m: RegExpExecArray } | null = null;
  const seen = new Set<Template>();
  for (const w of new Set(words(core))) {
    for (const t of byWord.get(w) || []) {
      if (seen.has(t)) continue;
      seen.add(t);
      if (best && t.size <= best.t.size) continue;
      const m = t.re.exec(core);
      if (m) best = { t, m };
    }
  }
  if (!best) return undefined;
  const { t, m } = best;
  return t.out.replace(/\{(\d+)\}/g, (_, i) => {
    const v = m[Number(i) + 1] ?? "";
    if (t.plural.has(Number(i)) && /^(s|es|ies)?$/.test(v)) return "";
    // A value that is itself wording ("In Progress") is translated; numbers, names and codes stay as they are.
    const n = norm(v);
    return (n && /[A-Za-z]/.test(n) && depth < 2 ? translateCore(n, depth + 1) : undefined) ?? v;
  });
}
/** The translation of one piece of English wording (keeping its spaces), or null when there is none. */
export function translateText(text: string): string | null {
  const core = norm(text);
  if (!core || !/[A-Za-z]/.test(core)) return null;
  const out = translateCore(core);
  if (out === undefined) return null;
  return (text.match(/^\s*/)?.[0] || "") + out + (text.match(/\s*$/)?.[0] || "");
}

const skipped = (el: Element | null) => !!el?.closest(NO_TRANSLATE);

function doText(n: Text): void {
  if (doneText.get(n) === n.data) return;
  const tr = translateText(n.data);
  if (tr !== null && tr !== n.data) { n.data = tr; doneText.set(n, tr); }
}
function doAttrs(el: Element): void {
  const done = doneAttr.get(el) || {};
  const names = el.tagName === "INPUT" && /^(button|submit|reset)$/i.test((el as HTMLInputElement).type) ? [...ATTRS, "value"] : el.tagName === "OPTGROUP" ? [...ATTRS, "label"] : ATTRS;
  for (const a of names) {
    const v = el.getAttribute(a);
    if (!v || done[a] === v) continue;
    const tr = translateText(v);
    if (tr !== null && tr !== v) { el.setAttribute(a, tr); done[a] = tr; doneAttr.set(el, done); }
  }
}
/** Everything inside a part of the page that may be translated. */
function doTree(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) { if (!skipped(root.parentElement)) doText(root as Text); return; }
  if (!(root instanceof Element) || skipped(root)) return;
  doAttrs(root);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeType === Node.ELEMENT_NODE && (n as Element).matches(NO_TRANSLATE) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) doText(n as Text);
    else doAttrs(n as Element);
  }
}

function onMutations(list: MutationRecord[]): void {
  // Signed out (the sign-in screens are English) or the language changed: nothing more is translated here.
  if (currentLang() !== active) { stop(); return; }
  for (const m of list) {
    if (m.type === "childList") m.addedNodes.forEach((n) => doTree(n));
    else if (m.type === "characterData") { const n = m.target as Text; if (!skipped(n.parentElement)) doText(n); }
    else if (m.type === "attributes" && m.target instanceof Element && !skipped(m.target)) doAttrs(m.target);
  }
}

function setPageLanguage(code: string): void {
  const l = langInfo(code);
  const html = document.documentElement;
  html.lang = l.code === "zh" ? "zh-CN" : l.code === "en" ? "en-GB" : l.code;
  html.dir = l.dir;
  for (const c of Array.from(html.classList)) if (c.startsWith("screen-lang-")) html.classList.remove(c);
  html.classList.toggle("screen-lang", l.code !== "en");
  html.classList.toggle("screen-rtl", l.dir === "rtl");
  if (l.code !== "en") html.classList.add(`screen-lang-${l.code}`);
}

function stop(): void {
  observer?.disconnect();
  observer = null;
  active = "en";
  setPageLanguage("en");
}

/** Before each screen is drawn: the person's language is ready (its wording loaded once), English otherwise. */
export async function ensureScreenLanguage(): Promise<void> {
  syncDeviceLang();
  const want = currentLang();
  if (want === active) return;
  if (want === "en") { stop(); return; }
  try {
    const r = await fetch(`/i18n/${encodeURIComponent(want)}.json?v=${encodeURIComponent(state.session.build || state.session.version || "")}`, { credentials: "same-origin" });
    if (!r.ok) throw new Error(String(r.status));
    load(await r.json());
  } catch {
    stop();   // the wording couldn't be loaded: the screens stay English rather than half-translated
    return;
  }
  active = want;
  setPageLanguage(want);
  observer?.disconnect();
  observer = new MutationObserver(onMutations);
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS, "value", "label"] });
  doTree(document.body);
}
